import type { QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  attributeReferral,
  getPendingReferralCode,
  removePendingReferralCode,
} from "@/lib/referral";
import {
  selectHouseholdPayload,
  useOnboarding,
} from "@/features/onboarding/store";
import type { OnboardingPayload, OnboardingResponse } from "@/types/profile";
import { claimPersistedState, waitForHydration } from "./userScope";

/**
 * Post-sign-in work shared by every auth flow (audit H1 / H2 / L1):
 * magic link and web OAuth land on `/auth/callback`; native Google/Apple
 * id-token sign-in and the OTP code never leave `/login`. Both call this.
 *
 *  1. Sync the anonymous onboarding profile (household + food exclusions)
 *     completed before sign-in, then reset the local wizard so it is never
 *     re-posted on a later sign-in.
 *  2. Attribute a pending referral code.
 *  3. Decide where to go next.
 */
export interface AfterAuthResult {
  /** Route to navigate to (replace). */
  destination: string;
  /** True when the onboarding profile could not be saved — the wizard is
   *  kept intact and the user is sent back to its review step to retry. */
  onboardingSyncFailed: boolean;
}

export const DEFAULT_AUTHED_ROUTE = "/menu";

/** Only accept same-app absolute paths (no protocol-relative `//host`). */
export function safeRedirectPath(from: unknown): string | null {
  if (typeof from !== "string") return null;
  if (!from.startsWith("/") || from.startsWith("//")) return null;
  if (from.startsWith("/login") || from.startsWith("/auth/callback")) {
    return null;
  }
  return from;
}

async function syncOnboarding(): Promise<"synced" | "skipped" | "failed"> {
  await waitForHydration(useOnboarding);
  const state = useOnboarding.getState();
  if (state.step !== "review") return "skipped";

  try {
    await api<OnboardingResponse>("/api/v1/onboarding", {
      method: "POST",
      body: {
        // HouseholdProfile is `extra="forbid"` server-side: never send the
        // local-only `foodExclusions` field in this payload.
        household: selectHouseholdPayload(state),
      } satisfies OnboardingPayload,
    });
    if (state.foodExclusions.length > 0) {
      await api<string[]>("/api/v1/food-exclusions/bulk", {
        method: "POST",
        body: { items: state.foodExclusions },
      });
    }
  } catch {
    return "failed";
  }
  useOnboarding.getState().reset();
  return "synced";
}

async function consumeReferral(): Promise<void> {
  // Read first, delete only after confirmed attribution so a network
  // failure doesn't permanently lose the code.
  try {
    const code = await getPendingReferralCode();
    if (code) {
      await attributeReferral(code);
      await removePendingReferralCode();
    }
  } catch {
    // best-effort — referral attribution failure must not block navigation
  }
}

export interface AfterAuthOptions {
  /** Supabase id of the user who just signed in. */
  userId: string;
  queryClient: QueryClient;
  /** Where RequireAuth came from (`location.state.from`). */
  from?: unknown;
}

async function run({
  userId,
  queryClient,
  from,
}: AfterAuthOptions): Promise<AfterAuthResult> {
  // Scope the persisted stores to this user FIRST (M28): if they still hold
  // another account's state, its onboarding wizard must be wiped before we
  // read it, or it would be posted as this user's household.
  await claimPersistedState(userId, queryClient);
  const onboarding = await syncOnboarding();
  await consumeReferral();

  if (onboarding === "synced") {
    return { destination: "/menu?generate=true", onboardingSyncFailed: false };
  }
  if (onboarding === "failed") {
    return { destination: "/onboarding", onboardingSyncFailed: true };
  }
  return {
    destination: safeRedirectPath(from) ?? DEFAULT_AUTHED_ROUTE,
    onboardingSyncFailed: false,
  };
}

let inflight: Promise<AfterAuthResult> | null = null;

/**
 * Single-flight: `/auth/callback` and the `/login` session watcher (and
 * React StrictMode's double effects) may race; the sync must run once.
 */
export function runAfterAuth(
  options: AfterAuthOptions,
): Promise<AfterAuthResult> {
  if (!inflight) {
    inflight = run(options).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}
